import { useModalContext } from 'common/Context/ModalContext';
import FeatureTile from 'components/FeatureTiles/FeatureTile';
import { Feature } from 'components/FeatureTiles/FeatureTiles';
import React, { ReactNode, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import type { BoostPeakCallToActionProps } from '../../../types';
import styles from './BoostPeakCallToAction.module.scss';

interface InstructionText {
  text: string;
  link?: {
    url: string;
    handleOnClick: () => void;
  };
}

interface InstructionProps {
  index: string;
  instruction: InstructionText[];
  children?: ReactNode;
}

const Instruction: React.FC<InstructionProps> = ({ index, instruction, children }) => {
  return (
    <div className={styles.Instruction}>
      <div className={styles.InstructionText}>
        <span className={styles.Index}>{index}</span>
        <div>
          {instruction.map(({ text, link }) => {
            return link ? (
              <Link to={link.url} onClick={() => link.handleOnClick()} key={text}>
                {text}
              </Link>
            ) : (
              <span key={text}>{text}</span>
            );
          })}
        </div>
      </div>
      {!!children && <div className={styles.Children}>{children}</div>}
    </div>
  );
};

const BoostPeakCallToAction: React.FC<BoostPeakCallToActionProps> = ({ venueId }) => {
  const { closeModal } = useModalContext();

  const boostMenuPricing: Feature = useMemo(() => {
    return {
      icon: 'mm-percentage',
      heading: 'Boost menu pricing',
      subheading: 'Temporarily increase your menu prices',
      link: `venue/${venueId}/pricing`,
      featureFlag: 'surchargeEnabled'
    };
  }, [venueId]);

  const closeRef = useRef<HTMLButtonElement>(null);

  return (
    <div className={styles.CallToActionContainer}>
      <div className={styles.Intro}>
        <h3>How to boost your pricing with Doshii?</h3>
      </div>
      <div className={styles.CornerContainer}>
        <button
          className={styles.CloseButton}
          type="button"
          aria-label="Close Modal"
          data-testid="corner-close-button"
          onClick={closeModal}
          ref={closeRef}
          tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
        >
          <i className="mm-times" />
        </button>
      </div>
      <div>
        <Instruction
          index="1."
          instruction={[
            {
              text: 'Pop in here just before the suggested time period and '
            },
            {
              text: 'navigate to boost menu pricing',
              link: {
                url: boostMenuPricing.link,
                handleOnClick: () => {
                  logAmplitudeEvent({
                    name: 'click setup offer link',
                    category: AmplitudeEventCategories.BOOST,
                    properties: {
                      [AmplitudeEventProperties.ENTITY]: 'boost menu pricing link 1'
                    }
                  });
                  closeModal();
                }
              }
            },
            {
              text: ' (you can also access this via the menu manager page)'
            }
          ]}
        >
          <FeatureTile
            icon={boostMenuPricing.icon}
            heading={boostMenuPricing.heading}
            subheading={boostMenuPricing.subheading}
            link={boostMenuPricing.link}
            handleOnClick={() => {
              logAmplitudeEvent({
                name: 'click setup offer link',
                category: AmplitudeEventCategories.BOOST,
                properties: {
                  [AmplitudeEventProperties.ENTITY]: 'boost menu pricing button'
                }
              });
              closeModal();
            }}
          />
        </Instruction>
        <Instruction
          index="2."
          instruction={[
            {
              text: 'Activate boost pricing for your menus by increasing the prices across your menus by %'
            }
          ]}
        >
          <img src="/Images/ActivateBoostPricing.svg" alt="Screenshot of surge pricing" />
        </Instruction>
        <Instruction
          index="3."
          instruction={[
            {
              text: 'After the suggested boost period has elapsed you will need pop back to your '
            },
            {
              text: " menu's boost pricing",
              link: {
                url: boostMenuPricing.link,
                handleOnClick: () => {
                  logAmplitudeEvent({
                    name: 'click setup offer link',
                    category: AmplitudeEventCategories.BOOST,
                    properties: {
                      [AmplitudeEventProperties.ENTITY]: 'boost menu pricing link 2'
                    }
                  });
                  closeModal();
                }
              }
            },
            {
              text: ` and set your prices back to the "Don't increase" option to return to your standard pricing.`
            }
          ]}
        />
      </div>
      <div className={styles.Footer}>
        <span>
          Interested in the ability to automate boost pricing?&nbsp;
          <a
            href="https://portal.productboard.com/brxcizuig4cocphxrh3sep8v/c/22-boost-peak-periods"
            target="_blank"
            onClick={() => {
              logAmplitudeEvent({
                name: 'click productboard vote here',
                category: AmplitudeEventCategories.BOOST,
                properties: {
                  [AmplitudeEventProperties.SOURCE]: 'boost high surge'
                }
              });
            }}
            rel="noreferrer"
          >
            Vote here
          </a>
        </span>
      </div>
    </div>
  );
};

export default BoostPeakCallToAction;
